'use client';

import React from 'react';

const FailurePatterns = () => {
  const patterns = [
    {
      title: 'Adoption Debt',
      definition: 'The growing gap between AI capability and real organisational use.',
      description: 'As AI capabilities advance faster than organisations can absorb them, adoption debt accumulates. This gap represents unrealised value, wasted investment, and increasing competitive disadvantage. It is not a technical problem — it is a leadership and governance problem.',
    },
    {
      title: 'Pilot Graveyards',
      definition: 'Proofs of concept that succeed technically but never scale.',
      description: 'Organisations launch AI pilots that demonstrate technical feasibility but fail to achieve organisation-wide adoption. These pilots succeed in isolation but die in the transition to scale — not because the technology failed, but because the organisation was not ready to change.',
    },
    {
      title: 'Leadership Latency',
      definition: 'The delay between AI potential and organisational readiness to act.',
      description: 'Leadership teams recognise AI\'s strategic importance but lack the systems, language, and governance to act decisively. This latency creates strategic drift, missed opportunities, and organisational uncertainty. It reflects inadequate leadership capability, not inadequate technology.',
    },
  ];

  return (
    <section className="py-24 lg:py-32 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold font-headline text-[#0A2540] mb-12 leading-tight">
            Common Failure Patterns in AI Adoption
          </h2>

          <div className="mb-12 text-lg text-gray-800 font-body leading-relaxed">
            <p>
              The following patterns are observed consistently across industries and public institutions. They are not execution mistakes. They are predictable outcomes of inadequate leadership systems and governance structures.
            </p>
          </div>

          <div className="space-y-12">
            {patterns.map((pattern, index) => (
              <div key={index} className="border-2 border-gray-200 p-8 hover:border-[#0A2540] transition-colors duration-300">
                <h3 className="text-3xl font-bold text-[#0A2540] mb-4">
                  {pattern.title}
                </h3>
                <p className="text-xl font-semibold text-gray-700 mb-6 italic">
                  {pattern.definition}
                </p>
                <p className="text-lg text-gray-800 leading-relaxed">
                  {pattern.description}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-12 p-8 bg-gray-50 border-l-4 border-[#0A2540]">
            <p className="text-lg text-gray-800 leading-relaxed">
              These patterns represent systemic capability gaps, not individual failures. They require leadership-level intervention, not project-level fixes.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FailurePatterns;